import { Title, Stack, Group, Badge, NumberInput, Button, Alert, Modal, Code } from "@mantine/core";
import { DataTable } from "mantine-datatable";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { shadowDiffsApi, type ShadowDiff } from "../api/shadowDiffs.js";

const INITIAL_HOURS = 24;
const INITIAL_LIMIT = 500;

function colorFor(c: ShadowDiff["classification"]): string {
  switch (c) {
    case "match": return "green";
    case "expected-diff": return "yellow";
    case "unexpected-diff": return "red";
    default: return "gray";
  }
}

export function ShadowCompare() {
  const [hours, setHours] = useState(INITIAL_HOURS);
  const [limit, setLimit] = useState(INITIAL_LIMIT);
  const [tick, setTick] = useState(0);
  const [selected, setSelected] = useState<ShadowDiff | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["shadow-diffs", hours, limit, tick],
    queryFn: () => shadowDiffsApi.query(hours, limit),
  });

  const counts = (data ?? []).reduce<Record<string, number>>((acc, d) => {
    acc[d.classification] = (acc[d.classification] ?? 0) + 1;
    return acc;
  }, {});

  const unexpected = counts["unexpected-diff"] ?? 0;

  return (
    <Stack>
      <Title order={2}>Shadow comparison</Title>
      <Group>
        <NumberInput label="Last X hours" min={1} max={168} value={hours} onChange={(v) => setHours(Number(v) || INITIAL_HOURS)} />
        <NumberInput label="Max rows" min={50} max={1000} value={limit} onChange={(v) => setLimit(Number(v) || INITIAL_LIMIT)} />
        <Button onClick={() => setTick(t => t + 1)}>Refresh</Button>
      </Group>
      <Group>
        {Object.entries(counts).map(([k, v]) => (
          <Badge key={k} color={colorFor(k as ShadowDiff["classification"])}>{k}: {v}</Badge>
        ))}
      </Group>
      {unexpected > 0 && (
        <Alert color="red" title="Unexpected diffs present">
          Investigate before flipping to V7_LIVE. Click any row to see the full V6/V7 decision payloads.
        </Alert>
      )}
      <DataTable
        striped withTableBorder fetching={isLoading} idAccessor="messageId"
        records={data ?? []}
        onRowClick={({ record }) => setSelected(record)}
        columns={[
          { accessor: "classification", title: "Class", width: 130, render: (d) => <Badge color={colorFor(d.classification)}>{d.classification}</Badge> },
          { accessor: "messageId", title: "Message ID", render: (d) => <code style={{ fontSize: 11 }}>{d.messageId.slice(0, 24)}…</code> },
          { accessor: "v6folder", title: "V6 folder", render: (d) => d.v6?.decidedFolderId?.slice(0, 16) ?? "—" },
          { accessor: "v7folder", title: "V7 folder", render: (d) => d.v7?.decidedFolderId?.slice(0, 16) ?? "—" },
          { accessor: "v6fwd", title: "V6 fwd", render: (d) => d.v6?.forwardedToSupport ? "✓" : "—" },
          { accessor: "v7fwd", title: "V7 fwd", render: (d) => d.v7?.forwardedToSupport ? "✓" : "—" },
          { accessor: "reasons", title: "Notes", ellipsis: true, render: (d) => d.reasons.join("; ") },
        ]}
      />
      <Modal opened={!!selected} onClose={() => setSelected(null)} title="Diff detail" size="xl">
        <Code block>{JSON.stringify(selected, null, 2)}</Code>
      </Modal>
    </Stack>
  );
}
